// V1.0.65: Modal Chia sẻ tài liệu — chỉ owner mới thấy.
// Tính năng: list các share hiện tại + thêm share mới với expires_at tùy chọn.
import { useState, useMemo } from 'react';
import {
  Modal, Select, Button, DatePicker, Checkbox, Input, Empty, Spin, App as AntdApp,
  Tooltip, Popconfirm, Tag, Avatar, Space,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, Clock, Download, Link as LinkIcon } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import { documentService, type DocumentShare } from '@/services/documentService';
import { userService } from '@/services/userService';

interface Props {
  open: boolean;
  documentId: string;
  documentTitle: string;
  isOwner: boolean;
  onClose: () => void;
}

function getInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(-2).map(p => p[0]?.toUpperCase() ?? '').join('');
}

export function ShareDocumentModal({ open, documentId, documentTitle, isOwner, onClose }: Props) {
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  // Form thêm share
  const [newUserIds, setNewUserIds] = useState<string[]>([]);
  const [newExpiry, setNewExpiry] = useState<Dayjs | null>(null);
  const [newNoExpiry, setNewNoExpiry] = useState(true);
  const [newCanDownload, setNewCanDownload] = useState(true);
  const [newNote, setNewNote] = useState('');

  // Load shares hiện tại
  const { data: shares = [], isLoading } = useQuery({
    queryKey: ['document-shares', documentId],
    queryFn: () => documentService.listShares(documentId),
    enabled: open && !!documentId,
  });

  // Load users (cho picker)
  const { data: usersResp } = useQuery({
    queryKey: ['users', 'for-share-picker'],
    queryFn: () => userService.list({ pageSize: 1000 }),
    enabled: open && isOwner,
  });
  const allUsers = usersResp?.items ?? [];

  // Loại user đã có share active
  const sharedIds = useMemo(
    () => new Set(shares.filter(s => s.isActive).map(s => s.sharedWithUser.id)),
    [shares],
  );
  const addableUsers = useMemo(
    () => allUsers.filter(u => !sharedIds.has(u.id)),
    [allUsers, sharedIds],
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      if (newUserIds.length === 0) throw new Error('Chưa chọn user nào');
      const expIso = newNoExpiry ? null : (newExpiry ? newExpiry.toISOString() : null);
      if (!newNoExpiry && !newExpiry) {
        throw new Error('Chưa chọn thời hạn hoặc tick "Không thời hạn"');
      }
      await documentService.createShares(documentId, {
        userIds: newUserIds,
        expiresAt: expIso,
        canDownload: newCanDownload,
        note: newNote.trim() || null,
      });
    },
    onSuccess: () => {
      message.success(`Đã chia sẻ với ${newUserIds.length} người`);
      setNewUserIds([]);
      setNewExpiry(null);
      setNewNoExpiry(true);
      setNewCanDownload(true);
      setNewNote('');
      qc.invalidateQueries({ queryKey: ['document-shares', documentId] });
      qc.invalidateQueries({ queryKey: ['document-detail', documentId] });
    },
    onError: (e: any) => {
      message.error('Thất bại: ' + (e?.response?.data?.message ?? e.message));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => documentService.revokeShare(shareId),
    onSuccess: () => {
      message.success('Đã thu hồi chia sẻ');
      qc.invalidateQueries({ queryKey: ['document-shares', documentId] });
    },
    onError: (e: any) => {
      message.error('Thất bại: ' + (e?.response?.data?.message ?? e.message));
    },
  });

  // Sort: active trước, theo shared_at desc
  const sortedShares = useMemo(() => {
    return [...shares].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime();
    });
  }, [shares]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      title={
        <span>
          <LinkIcon size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
          Chia sẻ tài liệu — {documentTitle}
        </span>
      }
      width={760}
      destroyOnClose
    >
      {/* Form thêm chia sẻ (chỉ owner) */}
      {isOwner && (
        <div style={{
          padding: 14,
          background: '#f7faff',
          border: '1px solid #d6e4ff',
          borderRadius: 6,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1976D2', marginBottom: 10 }}>
            <UserPlus size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            CHIA SẺ MỚI
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 4 }}>Người được chia sẻ</div>
            <Select
              mode="multiple"
              showSearch
              placeholder="Chọn 1 hoặc nhiều người..."
              value={newUserIds}
              onChange={setNewUserIds}
              style={{ width: '100%' }}
              optionFilterProp="label"
              options={addableUsers.map(u => ({
                value: u.id,
                label: `${u.fullName}${u.position ? ` — ${u.position}` : ''}`,
              }))}
              notFoundContent={addableUsers.length === 0 ? 'Tất cả user đã được chia sẻ' : undefined}
              maxTagCount={5}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px' }}>
              <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 4 }}>Thời hạn</div>
              <Space>
                <Checkbox checked={newNoExpiry} onChange={(e) => setNewNoExpiry(e.target.checked)}>
                  Không thời hạn
                </Checkbox>
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Chọn ngày hết hạn"
                  disabled={newNoExpiry}
                  value={newExpiry}
                  onChange={setNewExpiry}
                  disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                />
              </Space>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 4 }}>Quyền tải về</div>
              <Checkbox checked={newCanDownload} onChange={(e) => setNewCanDownload(e.target.checked)}>
                <Download size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                Cho phép tải về
              </Checkbox>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 4 }}>Ghi chú (tùy chọn)</div>
            <Input.TextArea
              rows={2}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Ghi chú lý do chia sẻ..."
              maxLength={500}
              showCount
            />
          </div>

          <div style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<UserPlus size={14} />}
              loading={createMutation.isPending}
              disabled={newUserIds.length === 0}
              onClick={() => createMutation.mutate()}
            >
              Chia sẻ với {newUserIds.length} người
            </Button>
          </div>
        </div>
      )}

      {/* Danh sách share */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#5b6875', marginBottom: 8 }}>
        DANH SÁCH CHIA SẺ ({sortedShares.length})
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : sortedShares.length === 0 ? (
        <Empty description="Chưa chia sẻ với ai" />
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #f0f4f8', borderRadius: 4 }}>
          {sortedShares.map((s, idx) => {
            const expiresLabel = s.expiresAt
              ? dayjs(s.expiresAt).format('DD/MM/YYYY HH:mm')
              : 'Không thời hạn';
            const isExpired = !!s.expiresAt && dayjs(s.expiresAt).isBefore(dayjs());
            return (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderBottom: idx < sortedShares.length - 1 ? '1px solid #f0f4f8' : 'none',
                  background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                  opacity: s.isActive ? 1 : 0.55,
                }}
              >
                <Avatar
                  size={36}
                  src={s.sharedWithUser.avatarUrl ?? undefined}
                  style={{ background: '#1976D2', flexShrink: 0 }}
                >
                  {!s.sharedWithUser.avatarUrl && getInitials(s.sharedWithUser.fullName)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1e23' }}>
                    {s.sharedWithUser.fullName}
                    {!s.isActive && (
                      <Tag color="default" style={{ marginLeft: 8 }}>
                        {isExpired ? 'Hết hạn' : 'Đã thu hồi'}
                      </Tag>
                    )}
                    {s.canDownload && s.isActive && (
                      <Tooltip title="Có quyền tải về">
                        <Download size={12} style={{ marginLeft: 6, color: '#1976D2', verticalAlign: -1 }} />
                      </Tooltip>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#7d8a98', display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
                    <span>
                      <Clock size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
                      Hết hạn: {expiresLabel}
                    </span>
                    <span>
                      Chia sẻ {dayjs(s.sharedAt).format('DD/MM/YYYY HH:mm')} bởi {s.sharedByUser.fullName}
                    </span>
                  </div>
                  {s.note && (
                    <div style={{ fontSize: 11, color: '#5b6875', marginTop: 4, fontStyle: 'italic' }}>
                      "{s.note}"
                    </div>
                  )}
                </div>
                {isOwner && s.isActive && (
                  <Popconfirm
                    title="Thu hồi chia sẻ?"
                    description={`${s.sharedWithUser.fullName} sẽ không xem được tài liệu nữa.`}
                    okText="Thu hồi"
                    okType="danger"
                    cancelText="Hủy"
                    onConfirm={() => revokeMutation.mutate(s.id)}
                  >
                    <Tooltip title="Thu hồi chia sẻ">
                      <Button
                        icon={<Trash2 size={14} />}
                        danger
                        size="small"
                        type="text"
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
